import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Factory, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function Login() {
  const { completeLogin, user, setUser } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [step, setStep] = useState("cred"); // cred | otp
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSessionId, setOtpSessionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [otpLeft, setOtpLeft] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem("oa_session_expired")) {
      sessionStorage.removeItem("oa_session_expired");
      toast.error("Your session has expired. Please login again.");
    }
  }, []);

  // Already authenticated → never show the login form on top of a live session.
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (otpLeft <= 0) return;
    const iv = setInterval(() => setOtpLeft((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(iv);
  }, [otpLeft]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const iv = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(iv);
  }, [cooldown]);

  const submitCred = async (e) => {
    e.preventDefault();
    // A new login attempt must never inherit a previous account's session state.
    localStorage.removeItem("oa_token");
    setUser(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { loginId, password });
      setOtpSessionId(data.otpSessionId);
      setOtpLeft(data.expiresIn || 300);
      setCooldown(60);
      setStep("otp");
      toast.success("Verification code emailed to you.");
    } catch (e) {
      toast.error(errMsg(e, "Invalid Login ID or Password."));
    }
    setBusy(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otpSessionId, otp });
      completeLogin(data.access_token, data.user, data.expiresIn);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(errMsg(e, "Invalid or Expired OTP."));
    }
    setBusy(false);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      const { data } = await api.post("/auth/resend-otp", { otpSessionId });
      setOtpLeft(data.expiresIn || 300);
      setOtp("");
      toast.success("A new verification code has been sent.");
      setCooldown(60);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          {branding.logo ? (
            <img src={branding.logo} alt="logo" className="h-11 w-11 rounded-lg object-contain bg-white/10 p-1" />
          ) : (
            <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ background: "var(--brand)" }}>
              <Factory className="h-6 w-6" />
            </div>
          )}
          <div>
            <div className="font-head font-bold text-lg">{branding.companyName}</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">{branding.caNumber}</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-head text-4xl font-extrabold leading-tight">Order Acceptance<br />Management System</h1>
          <p className="mt-4 text-slate-300 max-w-md">Create, control and print official Order Acceptance documents with secure OTP-protected access.</p>
          <div className="mt-8 flex gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-500" /> OTP Secured</span>
            <span className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-500" /> Role Based</span>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© {new Date().getFullYear()} Aurombica Engineering Co. Pvt. Ltd.</div>
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-amber-600/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-100">
        <div className="w-full max-w-md">
          {step === "cred" ? (
            <form onSubmit={submitCred} className="card p-8" data-testid="login-form">
              <h2 className="font-head text-2xl font-bold text-slate-900">Sign in</h2>
              <p className="text-sm text-slate-500 mt-1 mb-6">Enter your credentials to continue</p>
              <label className="label">Login ID / Email</label>
              <input className="input mb-4" value={loginId} onChange={(e) => setLoginId(e.target.value)} placeholder="you@company.com" data-testid="login-id-input" required />
              <label className="label">Password</label>
              <input className="input mb-6" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" required />
              <button className="btn-primary w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Validating…" : "Continue"}</button>
              <div className="text-center mt-4">
                <Link to="/forgot-password" className="text-sm text-amber-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="card p-8" data-testid="otp-form">
              <button type="button" onClick={() => setStep("cred")} className="text-sm text-slate-500 flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
              <h2 className="font-head text-2xl font-bold text-slate-900">Enter Verification Code</h2>
              <p className="text-sm text-slate-500 mt-1 mb-4">A 6-digit code was sent to your registered email. It expires in 5 minutes.</p>
              <label className="label">6-Digit OTP</label>
              <input className="input mb-3 font-mono text-lg tracking-[0.5em] text-center" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="••••••" data-testid="otp-input" required />
              <div className="flex items-center justify-between text-xs text-slate-500 mb-4" data-testid="otp-timers">
                <span>{otpLeft > 0 ? `OTP expires in ${fmt(otpLeft)}` : "OTP expired — please resend"}</span>
                <span>{cooldown > 0 ? `Resend in ${fmt(cooldown)}` : "You can resend now"}</span>
              </div>
              <button className="btn-primary w-full" disabled={busy} data-testid="otp-submit-button">{busy ? "Verifying…" : "Verify OTP"}</button>
              <button type="button" onClick={resend} disabled={cooldown > 0} className="btn-ghost w-full mt-2" data-testid="resend-otp-button">
                {cooldown > 0 ? `Resend OTP in ${fmt(cooldown)}` : "Resend OTP"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
